import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AppLayout from '../../components/layout/AppLayout';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3002';

const CATEGORY_COLORS = {
  Career: '#0052CC',
  Sales: '#16a34a',
  Communication: '#d97706',
  Language: '#7c3aed',
  Clinical: '#dc2626',
};

const styles = {
  header: { marginBottom: '32px' },
  title: { fontSize: '24px', fontWeight: '700', color: '#111827' },
  sub: { fontSize: '15px', color: '#6b7280', marginTop: '6px', lineHeight: '1.6' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' },
  card: {
    backgroundColor: '#fff', borderRadius: '16px', padding: '28px',
    border: '1.5px solid #e5e7eb', cursor: 'pointer',
    transition: 'all 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  cardHover: { boxShadow: '0 8px 24px rgba(0,82,204,0.12)', transform: 'translateY(-2px)', borderColor: '#0052CC' },
  icon: { fontSize: '36px', marginBottom: '16px' },
  cardTitle: { fontSize: '18px', fontWeight: '700', color: '#111827', marginBottom: '8px' },
  cardDesc: { fontSize: '14px', color: '#4b5563', lineHeight: '1.6', marginBottom: '16px' },
  roles: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  roleBadge: {
    fontSize: '11px', fontWeight: '600', padding: '3px 10px',
    borderRadius: '20px', backgroundColor: '#f3f4f6', color: '#374151',
  },
  categoryBadge: {
    fontSize: '11px', fontWeight: '700', padding: '3px 10px',
    borderRadius: '20px', marginBottom: '12px', display: 'inline-block',
  },
  configModal: {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200,
  },
  modalCard: {
    backgroundColor: '#fff', borderRadius: '16px', padding: '32px',
    width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto',
    boxShadow: '0 24px 64px rgba(0,0,0,0.2)',
  },
  modalTitle: { fontSize: '22px', fontWeight: '700', color: '#111827', marginBottom: '6px' },
  modalSub: { fontSize: '14px', color: '#6b7280', marginBottom: '24px' },
  label: { fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' },
  input: {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    border: '1px solid #d1d5db', fontSize: '14px', marginBottom: '16px',
    boxSizing: 'border-box', outline: 'none',
  },
  select: {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    border: '1px solid #d1d5db', fontSize: '14px', marginBottom: '16px',
    boxSizing: 'border-box', outline: 'none', backgroundColor: '#fff',
  },
  sectionTitle: {
    fontSize: '13px', fontWeight: '700', color: '#0052CC',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    marginBottom: '12px', marginTop: '8px',
  },
  actions: { display: 'flex', gap: '12px', marginTop: '8px' },
  startBtn: {
    flex: 1, backgroundColor: '#0052CC', color: '#fff', border: 'none',
    borderRadius: '8px', padding: '12px', fontSize: '15px',
    fontWeight: '700', cursor: 'pointer',
  },
  cancelBtn: {
    backgroundColor: '#f3f4f6', color: '#374151', border: 'none',
    borderRadius: '8px', padding: '12px 20px', fontSize: '15px',
    fontWeight: '600', cursor: 'pointer',
  },
  voiceGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px',
  },
  voiceOption: {
    padding: '10px 12px', borderRadius: '8px', border: '2px solid #e5e7eb',
    cursor: 'pointer', fontSize: '13px', fontWeight: '500',
  },
  voiceOptionSelected: { border: '2px solid #0052CC', backgroundColor: '#EBF5FF', color: '#0052CC' },
  error: {
    backgroundColor: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626',
    borderRadius: '8px', padding: '10px 14px', fontSize: '13px', marginBottom: '12px',
  },
};

const LLM_PROVIDERS = [
  { id: 'anthropic', label: 'Anthropic', models: ['claude-sonnet-4-5', 'claude-haiku-4-5'] },
  { id: 'openai', label: 'OpenAI', models: ['gpt-4o', 'gpt-4o-mini'] },
  { id: 'google', label: 'Google', models: ['gemini-2.0-flash', 'gemini-2.5-pro'] },
];

export default function PracticeHome() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [config, setConfig] = useState({});
  const [hoveredCard, setHoveredCard] = useState(null);
  const [voices, setVoices] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [goals, setGoals] = useState('');
  const [voiceId, setVoiceId] = useState('');
  const [llmProvider, setLlmProvider] = useState('anthropic');
  const [llmModel, setLlmModel] = useState('claude-sonnet-4-5');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userInitiates, setUserInitiates] = useState(false);

  useEffect(() => { fetchTemplates(); fetchVoices(); fetchAgents(); }, []);

  async function fetchTemplates() {
    const res = await fetch(`${API}/api/practice/templates`);
    const data = await res.json();
    setTemplates(Array.isArray(data) ? data : []);
  }

  async function fetchVoices() {
    const res = await fetch(`${API}/api/voices`);
    const data = await res.json();
    setVoices(Array.isArray(data) ? data.slice(0, 20) : []);
  }

  async function fetchAgents() {
    const res = await fetch(`${API}/api/agents?owner_id=${currentUser.uid}`);
    const data = await res.json();
    setAgents(Array.isArray(data) ? data : []);
  }

  function openConfig(template) {
    setSelected(template);
    setConfig({});
    setError('');
  }

  async function handleStart() {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/practice/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          owner_id: currentUser.uid,
          template_id: selected.id,
          config,
          llm_provider: llmProvider,
          llm_model: llmModel,
          voice_id: voiceId,
          agent_id: selectedAgentId || null,
          goals: goals || '',
          user_initiates: userInitiates,
        }),
      });
      if (!res.ok) throw new Error('Failed to start session');
      const data = await res.json();
      navigate(`/practice/session/${data.session_id}`, {
  state: {
    opening_audio: data.opening_audio_url,
    opening_message: data.opening_message,
  }
});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AppLayout>
      <div style={styles.header}>
        <div style={styles.title}>Practice</div>
        <div style={styles.sub}>
          Choose a scenario and practice in a safe, realistic simulation — with voice input and AI feedback.
        </div>
      </div>

      <div style={styles.grid}>
        {templates.map(t => (
          <div
            key={t.id}
            style={{ ...styles.card, ...(hoveredCard === t.id ? styles.cardHover : {}) }}
            onMouseEnter={() => setHoveredCard(t.id)}
            onMouseLeave={() => setHoveredCard(null)}
            onClick={() => openConfig(t)}
          >
            <div style={{
              ...styles.categoryBadge,
              backgroundColor: `${CATEGORY_COLORS[t.category]}15`,
              color: CATEGORY_COLORS[t.category],
            }}>
              {t.category}
            </div>
            <div style={styles.icon}>{t.icon}</div>
            <div style={styles.cardTitle}>{t.title}</div>
            <div style={styles.cardDesc}>{t.description}</div>
            <div style={styles.roles}>
              <span style={styles.roleBadge}>You: {t.user_role}</span>
              <span style={styles.roleBadge}>AI: {t.ai_role}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Config Modal */}
      {selected && (
        <div style={styles.configModal} onClick={e => e.target === e.currentTarget && setSelected(null)}>
          <div style={styles.modalCard}>
            <div style={styles.modalTitle}>{selected.icon} {selected.title}</div>
            <div style={styles.modalSub}>{selected.description}</div>

            {error && <div style={styles.error}>{error}</div>}

            {/* Template config fields */}
            <div style={styles.sectionTitle}>Scenario Setup</div>
            {selected.config_fields.map(field => (
              <div key={field.key}>
                <label style={styles.label}>{field.label}</label>
                {field.type === 'select' ? (
                  <select
                    style={styles.select}
                    value={config[field.key] || ''}
                    onChange={e => setConfig(prev => ({ ...prev, [field.key]: e.target.value }))}
                  >
                    <option value="">Select...</option>
                    {field.options.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input
                    style={styles.input}
                    placeholder={field.placeholder}
                    value={config[field.key] || ''}
                    onChange={e => setConfig(prev => ({ ...prev, [field.key]: e.target.value }))}
                  />
                )}
              </div>
            ))}

            {/* Agent Library */}
            <div style={styles.sectionTitle}>AI Persona (Optional)</div>
            <label style={styles.label}>Use a saved agent as the AI counterpart</label>
            <select
              style={styles.select}
              value={selectedAgentId}
              onChange={e => {
                setSelectedAgentId(e.target.value);
                const agent = agents.find(a => a.id === e.target.value);
                if (agent?.voice_id) setVoiceId(agent.voice_id);
              }}
            >
              <option value="">Use template persona (default)</option>
              {agents.map(a => (
                <option key={a.id} value={a.id}>
                  {a.name}{a.role ? ` · ${a.role}` : ''}
                </option>
              ))}
            </select>
            {selectedAgentId && (
              <div style={{
                fontSize: '12px', color: '#6b7280', backgroundColor: '#f9fafb',
                padding: '10px 12px', borderRadius: '8px', marginBottom: '16px',
                border: '1px solid #e5e7eb',
              }}>
                {agents.find(a => a.id === selectedAgentId)?.system_prompt?.substring(0, 150)}...
              </div>
            )}

            {/* AI Voice */}
            <div style={styles.sectionTitle}>AI Voice</div>
            <label style={styles.label}>Select a voice for the AI</label>
            <select
              style={styles.select}
              value={voiceId}
              onChange={e => setVoiceId(e.target.value)}
            >
              <option value="">No voice (text only)</option>
              {voices.map(v => (
                <option key={v.voice_id} value={v.voice_id}>{v.name}</option>
              ))}
            </select>

            {/* LLM Provider */}
            <div style={styles.sectionTitle}>AI Model</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={styles.label}>Provider</label>
                <select
                  style={styles.select}
                  value={llmProvider}
                  onChange={e => {
                    setLlmProvider(e.target.value);
                    const first = LLM_PROVIDERS.find(p => p.id === e.target.value)?.models[0] || 'claude-sonnet-4-5';
                    setLlmModel(first);
                  }}
                >
                  {LLM_PROVIDERS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label style={styles.label}>Model</label>
                <select
                  style={styles.select}
                  value={llmModel}
                  onChange={e => setLlmModel(e.target.value)}
                >
                  {(LLM_PROVIDERS.find(p => p.id === llmProvider)?.models || []).map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Session Goals */}
            <div style={styles.sectionTitle}>Your Goals</div>
            <label style={styles.label}>What do you want to achieve in this session?</label>
            <textarea
              style={{
                ...styles.input,
                minHeight: '80px', resize: 'vertical', lineHeight: '1.6',
                fontFamily: 'inherit',
              }}
              placeholder="e.g. Practice handling salary objections confidently, use the STAR method for behavioral questions, stay calm under pressure..."
              value={goals}
              onChange={e => setGoals(e.target.value)}
            />
            <div style={styles.sectionTitle}>Who Speaks First?</div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
              {['AI', 'You'].map(opt => (
                <div
                  key={opt}
                  style={{
                    flex: 1, padding: '10px', borderRadius: '8px', textAlign: 'center',
                    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
                    border: (opt === 'You') === userInitiates ? '2px solid #0052CC' : '2px solid #e5e7eb',
                    backgroundColor: (opt === 'You') === userInitiates ? '#EBF5FF' : '#fff',
                    color: (opt === 'You') === userInitiates ? '#0052CC' : '#374151',
                  }}
                  onClick={() => setUserInitiates(opt === 'You')}
                >
                  {opt === 'AI' ? '🤖 AI speaks first' : '🎙 I speak first'}
                </div>
              ))}
            </div>
            <div style={styles.actions}>
              <button style={styles.cancelBtn} onClick={() => setSelected(null)}>Cancel</button>
              <button style={styles.startBtn} onClick={handleStart} disabled={loading}>
                {loading ? 'Starting...' : '▶ Start Practice'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
